import { saveAs } from 'file-saver'
import * as htmlToImage from 'html-to-image'
import { jsPDF } from 'jspdf'
import { RefObject, useCallback, useState } from 'react'

const PDF_PADDING_MM = 10
const A4_WIDTH_MM = 210
const PIXEL_RATIO = 2

async function buildFontEmbedCSS(): Promise<string> {
  const fontUrls: Record<string, string> = {
    normal: '/fonts/GT-Walsheim-Pro-Regular.woff2',
    bold: '/fonts/GT-Walsheim-Pro-Medium.woff2',
  }

  const faces: string[] = []

  for (const [weight, url] of Object.entries(fontUrls)) {
    try {
      const response = await fetch(url)
      if (!response.ok) continue

      const blob = await response.blob()
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(blob)
      })

      faces.push(`@font-face { font-family: Walsheim-Regular; src: url(${dataUrl}) format('woff2'); font-weight: ${weight}; }`)
    } catch {
      // skip unavailable font weights
    }
  }

  return faces.join('\n')
}

// Safari's foreignObject -> canvas drawImage path can drop cross-origin <img>
// elements even when html-to-image successfully inlines them as data URLs.
// We work around this by loading those images with CORS and overlaying them
// directly onto the canvas after html-to-image has produced its result.
async function loadCrossOriginImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = url
  })
}

function isCrossOriginImageSrc(src: string): boolean {
  if (!src) return false
  if (src.startsWith('data:') || src.startsWith('blob:')) return false
  try {
    const url = new URL(src, window.location.href)
    return url.origin !== window.location.origin
  } catch {
    return false
  }
}

export const useExportPdf = (ref: RefObject<HTMLElement | null>) => {
  const [isExporting, setIsExporting] = useState(false)

  const exportPdf = useCallback(
    async (filename: string = 'export.pdf') => {
      const node = ref.current
      if (!node) return

      setIsExporting(true)

      // Capture from an off-screen clone instead of mutating the live node. Showing the
      // `.pdf-export-only` header on the visible node reflowed the modal twice (on reveal and
      // on hide), which the user saw as the content "jumping" when clicking Download. The clone
      // is laid out off-screen at the same width, so the live modal never moves. As a bonus the
      // clone is static, so the overlay/link geometry can't be thrown off by the user scrolling
      // during the async capture window. (GR-2959)
      const sourceRect = node.getBoundingClientRect()
      const clone = node.cloneNode(true) as HTMLElement
      const offscreenContainer = document.createElement('div')
      offscreenContainer.style.position = 'fixed'
      offscreenContainer.style.top = '0'
      offscreenContainer.style.left = '0'
      offscreenContainer.style.transform = 'translateX(-200vw)'
      offscreenContainer.style.pointerEvents = 'none'
      offscreenContainer.style.zIndex = '-1'
      offscreenContainer.style.backgroundColor = '#ffffff'
      offscreenContainer.style.width = `${sourceRect.width}px`
      offscreenContainer.appendChild(clone)
      document.body.appendChild(offscreenContainer)

      try {
        clone.querySelectorAll<HTMLElement>('.pdf-export-only').forEach((el) => el.style.setProperty('display', 'block', 'important'))

        await new Promise((resolve) => requestAnimationFrame(resolve))

        const nodeRect = clone.getBoundingClientRect()
        const links = Array.from(clone.querySelectorAll<HTMLAnchorElement>('a[href]')).map((a) => {
          const rect = a.getBoundingClientRect()
          return {
            url: a.href,
            x: rect.left - nodeRect.left,
            y: rect.top - nodeRect.top,
            width: rect.width,
            height: rect.height,
          }
        })

        const crossOriginImages = await Promise.all(
          Array.from(clone.querySelectorAll<HTMLImageElement>('img'))
            .filter((el) => isCrossOriginImageSrc(el.src))
            .map(async (el) => ({ el, loaded: await loadCrossOriginImage(el.src) }))
        )
        const overlayImages = crossOriginImages.filter((entry): entry is { el: HTMLImageElement; loaded: HTMLImageElement } => entry.loaded !== null)

        const fontEmbedCSS = await buildFontEmbedCSS()

        const canvas = await htmlToImage.toCanvas(clone, {
          pixelRatio: PIXEL_RATIO,
          backgroundColor: '#ffffff',
          cacheBust: true,
          skipFonts: true,
          fontEmbedCSS,
          filter: (el) => !(el instanceof HTMLElement && el.classList.contains('pdf-export-hide')),
        })

        if (overlayImages.length > 0) {
          const ctx = canvas.getContext('2d')
          if (ctx) {
            const overlayNodeRect = clone.getBoundingClientRect()
            const scaleX = canvas.width / overlayNodeRect.width
            const scaleY = canvas.height / overlayNodeRect.height
            overlayImages.forEach(({ el, loaded }) => {
              const rect = el.getBoundingClientRect()
              const x = (rect.left - overlayNodeRect.left) * scaleX
              const y = (rect.top - overlayNodeRect.top) * scaleY
              const w = rect.width * scaleX
              const h = rect.height * scaleY
              if (w <= 0 || h <= 0) return
              try {
                ctx.drawImage(loaded, x, y, w, h)
              } catch {
                // ignore tainted-canvas / draw failures; html-to-image's output remains unchanged
              }
            })
          }
        }

        const contentWidthMm = A4_WIDTH_MM - PDF_PADDING_MM * 2
        const pxToMm = contentWidthMm / nodeRect.width
        const contentHeightMm = canvas.height / (canvas.width / contentWidthMm)
        const pageHeightMm = contentHeightMm + PDF_PADDING_MM * 2

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [A4_WIDTH_MM, pageHeightMm] })

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
        pdf.addImage(dataUrl, 'JPEG', PDF_PADDING_MM, PDF_PADDING_MM, contentWidthMm, contentHeightMm)

        links.forEach(({ url, x, y, width, height }) => {
          pdf.link(x * pxToMm + PDF_PADDING_MM, y * pxToMm + PDF_PADDING_MM, width * pxToMm, height * pxToMm, { url })
        })

        const pdfBlob = pdf.output('blob')
        saveAs(pdfBlob, filename)
      } finally {
        document.body.removeChild(offscreenContainer)
        setIsExporting(false)
      }
    },
    [ref]
  )

  return { exportPdf, isExporting }
}
